import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { HeroReveal, Pressable } from '@/components/motion'
import { CTASection } from '@/components/landing/CTASection'
import { Highlight } from '@/components/landing/about/Highlight'
import { WalkthroughMedia } from '@/components/landing/about/WalkthroughMedia'
import { BadgeStrip } from '@/components/landing/about/BadgeStrip'
import { TeamSection } from '@/components/landing/about/TeamSection'
import { JourneyTimeline } from '@/components/landing/about/JourneyTimeline'
import { CareersSection } from '@/components/landing/about/CareersSection'
import { PartnersSection } from '@/components/landing/about/PartnersSection'
import { ContactStrip } from '@/components/landing/about/ContactStrip'
import { companyInfo } from '@/data/about'

/**
 * About page, structured after the reference's /about (docs/design/
 * b2p-page-layouts.md section 5): statement hero, walkthrough media, badge
 * strip, team, journey timeline, careers, image + text, blue CTA band.
 */
export default function About() {
  return (
    <>
      <PageMeta
        title="About Templatean | Our Mission, Team, and Story"
        description="Learn about Templatean's mission to transform document workflows with AI. Meet our leadership team, explore open roles, and discover our company values."
      />

      <LandingLayout>
        {/* Hero: centred 60rem statement, quote + button row */}
        <section aria-labelledby="about-title" className="pb-[clamp(3rem,8vh,6rem)] pt-[clamp(4rem,12vh,8rem)]">
          <div className="landing-container">
            <div className="mx-auto max-w-[60rem]">
              <HeroReveal delay={0.05}>
                {/* companyInfo.tagline, split so two phrases carry highlight boxes */}
                <h1 id="about-title" className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white">
                  Documents that <Highlight color="pink">work</Highlight> as hard as{' '}
                  <Highlight color="blue">you do.</Highlight>
                </h1>
              </HeroReveal>
              <HeroReveal delay={0.2}>
                <div className="mt-12 flex flex-col gap-10 lg:mt-16 lg:flex-row lg:items-center lg:justify-between lg:gap-24">
                  <p className="max-w-[56ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg dark:border-[var(--color-landing-gray-700)] text-[color-mix(in_srgb,var(--color-landing-gray-900)_66%,transparent)] dark:text-[color-mix(in_srgb,white_62%,transparent)]">
                    {companyInfo.mission}
                  </p>
                  <Pressable className="shrink-0 self-start lg:self-auto">
                    <Link to="/contact" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                      Get in touch
                      <ArrowRight aria-hidden className="h-5 w-5" />
                    </Link>
                  </Pressable>
                </div>
              </HeroReveal>
            </div>
          </div>
        </section>

        {/* Media: product walkthrough in place of the reference's video */}
        <WalkthroughMedia />

        {/* Strip: where it runs and what it is certified for */}
        <BadgeStrip label="Inside the apps your team already uses. Built to enterprise standards." />

        <TeamSection />

        <JourneyTimeline />

        <CareersSection />

        <PartnersSection />

        <ContactStrip />

        <CTASection
          variant="accent"
          align="left"
          title="Ready to transform how your team works?"
          description="Join the beta program and be among the first to experience the future of document automation."
          buttons={[
            { label: 'Start free trial', href: '/register', variant: 'primary' },
            { label: 'Talk to sales', href: '/contact', variant: 'outline' },
          ]}
        />
      </LandingLayout>
    </>
  )
}
